import { useState, type FormEvent } from "react";
import { Building2, CreditCard, LayoutTemplate, Mail, Plus, Truck, X } from "lucide-react";
import { ApiError, type DeliveryOption, type ProductLayout, type Store, type StoreInput } from "../../lib/api";
import { parseMoney, toMajorInput } from "../../lib/money";
import { useUpdateStore } from "../../lib/store-api";
import { useAuthStore } from "../../stores/auth";
import { Button } from "../../components/ui/Button";

interface DeliveryDraft {
  id: string;
  name: string;
  description: string;
  fee: string;
  freeOver: string;
  pickup: boolean;
}

const CURRENCIES: StoreInput["currency"][] = ["NGN", "GHS", "KES", "ZAR", "USD"];

const PRODUCT_LAYOUTS: { id: ProductLayout; name: string; hint: string }[] = [
  { id: "classic", name: "Classic", hint: "Photo gallery beside the details" },
  { id: "gallery", name: "Gallery", hint: "All photos stacked, details stay in view" },
  { id: "centered", name: "Centered", hint: "One large photo, details centred below" },
];

/** A tiny sketch of each product page layout. */
function LayoutSketch({ layout }: { layout: ProductLayout }) {
  if (layout === "centered") {
    return (
      <span className="layout-sketch layout-sketch--centered" aria-hidden="true">
        <i className="sk-photo" />
        <i className="sk-line" />
        <i className="sk-line sk-line--short" />
        <i className="sk-btn" />
      </span>
    );
  }
  return (
    <span className={`layout-sketch layout-sketch--${layout}`} aria-hidden="true">
      <span className="sk-photos">
        <i className="sk-photo" />
        {layout === "gallery" ? <i className="sk-photo" /> : <i className="sk-thumbs" />}
      </span>
      <span className="sk-info">
        <i className="sk-line" />
        <i className="sk-line sk-line--short" />
        <i className="sk-btn" />
      </span>
    </span>
  );
}

function toDraft(option: DeliveryOption): DeliveryDraft {
  return {
    id: option.id,
    name: option.name,
    description: option.description ?? "",
    fee: toMajorInput(option.feeMinor),
    freeOver: toMajorInput(option.freeOverMinor ?? null),
    pickup: option.pickup ?? false,
  };
}

/** Store settings: notifications, currency, delivery, and payments. */
export function SettingsTab({ siteId, store }: { siteId: string; store: Store }) {
  const update = useUpdateStore(siteId);
  const userEmail = useAuthStore((s) => s.user?.email ?? "");

  const [notifyEmail, setNotifyEmail] = useState(store.notifyEmail || userEmail);
  const [currency, setCurrency] = useState(store.currency);
  const [productLayout, setProductLayout] = useState<ProductLayout>(store.productLayout ?? "classic");
  const [delivery, setDelivery] = useState<DeliveryDraft[]>(store.deliveryOptions.map(toDraft));
  const [acceptOnline, setAcceptOnline] = useState(store.acceptOnline);
  const [acceptBank, setAcceptBank] = useState(store.acceptBankTransfer);
  const [bank, setBank] = useState({
    bankName: store.bankDetails?.bankName ?? "",
    accountName: store.bankDetails?.accountName ?? "",
    accountNumber: store.bankDetails?.accountNumber ?? "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const setOption = (i: number, patch: Partial<DeliveryDraft>) => {
    setSaved(false);
    setDelivery((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaved(false);
    const options: DeliveryOption[] = [];
    for (const d of delivery) {
      const fee = d.pickup && !d.fee.trim() ? 0 : parseMoney(d.fee || "0");
      const freeOver = d.freeOver.trim() ? parseMoney(d.freeOver) : null;
      if (fee === null || (d.freeOver.trim() && freeOver === null)) {
        setError(`Check the amounts for "${d.name || "delivery option"}"`);
        return;
      }
      options.push({
        id: d.id,
        name: d.name.trim(),
        description: d.description.trim(),
        feeMinor: fee,
        ...(freeOver ? { freeOverMinor: freeOver } : {}),
        pickup: d.pickup,
      });
    }
    const hasBank = Object.values(bank).some((v) => v.trim());
    const input: StoreInput = {
      currency,
      notifyEmail: notifyEmail.trim(),
      deliveryOptions: options,
      bankDetails: hasBank ? bank : null,
      acceptBankTransfer: acceptBank,
      acceptOnline,
      productLayout,
    };
    try {
      await update.mutateAsync(input);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save settings.");
    }
  };

  return (
    <form className="settings-form" onSubmit={submit}>
      <section className="settings-card">
        <header>
          <Mail size={18} aria-hidden="true" />
          <div>
            <h2>Orders and currency</h2>
            <p className="text-muted text-sm">Where new-order emails go, and the currency your prices use.</p>
          </div>
        </header>
        <div className="form-row">
          <label className="form-field">
            <span className="form-label">Order notification email</span>
            <input
              className="form-input"
              type="email"
              placeholder="you@business.com"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
            />
          </label>
          <label className="form-field">
            <span className="form-label">Currency</span>
            <select className="form-input" value={currency} onChange={(e) => setCurrency(e.target.value as StoreInput["currency"])}>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="settings-card">
        <header>
          <LayoutTemplate size={18} aria-hidden="true" />
          <div>
            <h2>Product pages</h2>
            <p className="text-muted text-sm">How each product's page looks on your store.</p>
          </div>
        </header>
        <div className="layout-options" role="radiogroup" aria-label="Product page layout">
          {PRODUCT_LAYOUTS.map((option) => (
            <label key={option.id} className="layout-option">
              <input
                type="radio"
                name="productLayout"
                value={option.id}
                checked={productLayout === option.id}
                onChange={() => {
                  setSaved(false);
                  setProductLayout(option.id);
                }}
              />
              <LayoutSketch layout={option.id} />
              <strong>{option.name}</strong>
              <span className="text-muted text-sm">{option.hint}</span>
            </label>
          ))}
        </div>
      </section>

      <section className="settings-card">
        <header>
          <Truck size={18} aria-hidden="true" />
          <div>
            <h2>Delivery</h2>
            <p className="text-muted text-sm">Options shoppers choose from at checkout. Amounts in {currency}.</p>
          </div>
        </header>
        {delivery.map((d, i) => (
          <div key={d.id || i} className="delivery-row">
            <div className="form-row">
              <label className="form-field">
                <span className="form-label">Name</span>
                <input className="form-input" value={d.name} required onChange={(e) => setOption(i, { name: e.target.value })} />
              </label>
              <label className="form-field">
                <span className="form-label">Description</span>
                <input
                  className="form-input"
                  placeholder="e.g. 2–4 working days"
                  value={d.description}
                  onChange={(e) => setOption(i, { description: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row form-row--center">
              <label className="form-field">
                <span className="form-label">Fee</span>
                <input
                  className="form-input"
                  inputMode="decimal"
                  value={d.fee}
                  placeholder="0"
                  onChange={(e) => setOption(i, { fee: e.target.value })}
                />
              </label>
              <label className="form-field">
                <span className="form-label">Free over (optional)</span>
                <input
                  className="form-input"
                  inputMode="decimal"
                  value={d.freeOver}
                  onChange={(e) => setOption(i, { freeOver: e.target.value })}
                />
              </label>
              <label className="switch">
                <input type="checkbox" checked={d.pickup} onChange={(e) => setOption(i, { pickup: e.target.checked })} />
                <span>Pickup (no address)</span>
              </label>
              {delivery.length > 1 ? (
                <button
                  type="button"
                  className="icon-btn icon-btn--danger"
                  aria-label={`Remove ${d.name || "delivery option"}`}
                  onClick={() => setDelivery((prev) => prev.filter((_, j) => j !== i))}
                >
                  <X size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
        ))}
        {delivery.length < 6 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setDelivery((prev) => [...prev, { id: "", name: "", description: "", fee: "", freeOver: "", pickup: false }])}
          >
            <Plus size={14} aria-hidden="true" />
            Add delivery option
          </Button>
        ) : null}
      </section>

      <section className="settings-card">
        <header>
          <CreditCard size={18} aria-hidden="true" />
          <div>
            <h2>Payments</h2>
            <p className="text-muted text-sm">Choose how shoppers can pay. Turn on at least one.</p>
          </div>
        </header>
        <label className="payment-toggle">
          <input
            type="checkbox"
            checked={acceptOnline}
            disabled={!store.onlinePaymentsAvailable}
            onChange={(e) => setAcceptOnline(e.target.checked)}
          />
          <CreditCard size={20} aria-hidden="true" />
          <span>
            <strong>Online payments</strong>
            <span className="text-muted text-sm">
              {store.onlinePaymentsAvailable
                ? "Card, bank and USSD payments through our secure checkout."
                : "Not available yet — coming soon."}
            </span>
          </span>
        </label>
        <label className="payment-toggle">
          <input type="checkbox" checked={acceptBank} onChange={(e) => setAcceptBank(e.target.checked)} />
          <Building2 size={20} aria-hidden="true" />
          <span>
            <strong>Bank transfer</strong>
            <span className="text-muted text-sm">
              Shoppers see your account details after ordering. You mark orders paid once the money arrives.
            </span>
          </span>
        </label>
        {acceptBank ? (
          <div className="bank-fields">
            <div className="form-row">
              <label className="form-field">
                <span className="form-label">Bank name</span>
                <input
                  className="form-input"
                  value={bank.bankName}
                  required
                  onChange={(e) => setBank({ ...bank, bankName: e.target.value })}
                />
              </label>
              <label className="form-field">
                <span className="form-label">Account number</span>
                <input
                  className="form-input"
                  inputMode="numeric"
                  value={bank.accountNumber}
                  required
                  onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })}
                />
              </label>
            </div>
            <label className="form-field">
              <span className="form-label">Account name</span>
              <input
                className="form-input"
                value={bank.accountName}
                required
                onChange={(e) => setBank({ ...bank, accountName: e.target.value })}
              />
            </label>
          </div>
        ) : null}
      </section>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="settings-actions">
        {saved ? (
          <span className="text-muted text-sm" role="status">
            Settings saved
          </span>
        ) : null}
        <Button type="submit" loading={update.isPending}>
          Save settings
        </Button>
      </div>
    </form>
  );
}
